import type { ComponentType } from 'react';
import {
    createRootRoute,
    createRoute,
    createRouter,
    lazyRouteComponent,
    notFound,
    redirect,
    type AnyRoute,
} from '@tanstack/react-router';

import { ErrorPage, NotFoundPage, ProtectedLayout, RootLayout } from '@/layouts';
import { appConfig } from '@/lib/config';
import { acharSecao } from '@/lib/configuracoes/secoes';
import { ehTipoModelo } from '@/lib/contratos/modelos';
import { getManutencao } from '@/lib/manutencao';
import { getMe } from '@/lib/me';
import { HOME, LIVRES_NA_MANUTENCAO, REDIRECTS, ROUTE_ACCESS, type RotaPagina } from '@/route-access';

import type { Me } from '@alianca/core/contracts/me';

type Pagina = () => Promise<{ default: ComponentType }>;

/** Manutenção ligada: toda página vai para /manutencao (a API já responde 503; isto cobre também as páginas públicas). */
const root = createRootRoute({
    component: RootLayout,
    notFoundComponent: NotFoundPage,
    errorComponent: ErrorPage,
    beforeLoad: async ({ location }) => {
        if (LIVRES_NA_MANUTENCAO.includes(location.pathname)) return;
        if ((await getManutencao()).ativa) throw redirect({ href: '/manutencao' });
    },
});

/** Área logada: sem sessão ⇒ /login (o 401/403 com código já redirecionou no `api()`). */
const authed = createRoute({
    getParentRoute: () => root,
    id: 'authed',
    component: ProtectedLayout,
    beforeLoad: async () => {
        const me = await getMe();
        if (!me) throw redirect({ href: '/login' });
        if (me.emOnboarding) throw redirect({ href: '/boas-vindas' });
        return { me };
    },
});

/** Guard de UX por rota (T07 §3.5); o backend continua decidindo o acesso. */
function guard(path: RotaPagina) {
    const access = ROUTE_ACCESS[path];
    return ({ context }: { context: unknown }) => {
        if (!('personas' in access)) return;
        const me = (context as { me?: Me }).me;
        if (!me || !access.personas.includes(me.persona)) throw redirect({ href: HOME });
    };
}

function pagina(parent: AnyRoute, path: RotaPagina, load: Pagina, beforeLoad?: (a: { params: Record<string, string> }) => void) {
    const g = guard(path);
    return createRoute({
        getParentRoute: () => parent,
        path,
        beforeLoad: (args) => {
            g(args);
            beforeLoad?.({ params: args.params as Record<string, string> });
        },
        component: lazyRouteComponent(load),
    });
}

const redirecionar = (parent: AnyRoute, path: string, href: string) =>
    createRoute({
        getParentRoute: () => parent,
        path,
        beforeLoad: () => {
            throw redirect({ href });
        },
    });

const raiz = createRoute({
    getParentRoute: () => root,
    path: '/',
    beforeLoad: async () => {
        throw redirect({ href: (await getMe()) ? HOME : '/login' });
    },
});

const login = createRoute({
    getParentRoute: () => root,
    path: '/login',
    beforeLoad: async () => {
        if (await getMe().catch(() => null)) throw redirect({ href: HOME });
    },
    component: lazyRouteComponent(() => import('./app/login/page')),
});

const servicoValido = ({ params }: { params: Record<string, string> }) => {
    if (!ehTipoModelo(params.serviceType ?? '')) throw notFound();
};

const secaoValida = ({ params }: { params: Record<string, string> }) => {
    if (!acharSecao(params.secao, appConfig.appMode === 'staging')) throw notFound();
};

const publicas = [
    login,
    raiz,
    pagina(root, '/acionar-sinistro', () => import('./app/acionar-sinistro/page')),
    pagina(root, '/auth/callback', () => import('./app/auth/callback/page')),
    pagina(root, '/auth/complete-registration', () => import('./app/auth/complete-registration/page')),
    pagina(root, '/auth/reset-password', () => import('./app/auth/reset-password/page')),
    pagina(root, '/boas-vindas', () => import('./app/boas-vindas/page')),
    pagina(root, '/manutencao', () => import('./app/manutencao/page')),
];

const protegidas = [
    pagina(authed, '/carteira', () => import('./app/(protected)/carteira/page')),
    pagina(authed, '/carteira/$executivoId', () => import('./app/(protected)/carteira/[executivoId]/page')),
    pagina(authed, '/chargebacks', () => import('./app/(protected)/chargebacks/page')),
    pagina(authed, '/chargebacks/$id', () => import('./app/(protected)/chargebacks/[id]/page')),
    pagina(authed, '/comissoes', () => import('./app/(protected)/comissoes/page')),
    pagina(authed, '/comissoes/$tipo/$id', () => import('./app/(protected)/comissoes/[tipo]/[id]/page')),
    pagina(authed, '/configuracoes', () => import('./app/(protected)/configuracoes/page')),
    pagina(authed, '/configuracoes/$secao', () => import('./app/(protected)/configuracoes/[secao]/page'), secaoValida),
    pagina(authed, '/configuracoes/contratos/$serviceType', () => import('./app/(protected)/configuracoes/contratos/[serviceType]/page'), servicoValido),
    pagina(authed, '/contratos', () => import('./app/(protected)/contratos/page')),
    pagina(authed, '/contratos/$id', () => import('./app/(protected)/contratos/[id]/page')),
    pagina(authed, '/contratos/cotacao', () => import('./app/(protected)/contratos/cotacao/page')),
    pagina(authed, '/contratos/novo', () => import('./app/(protected)/contratos/novo/page')),
    pagina(authed, '/eletropostos/contratos/$id', () => import('./app/(protected)/eletropostos/contratos/[id]/page')),
    pagina(authed, '/eletropostos/contratos/novo', () => import('./app/(protected)/eletropostos/contratos/novo/page')),
    pagina(authed, '/eletropostos/cotacao', () => import('./app/(protected)/eletropostos/cotacao/page')),
    pagina(authed, '/eletropostos/propostas/$id', () => import('./app/(protected)/eletropostos/propostas/[id]/page')),
    pagina(authed, '/eletropostos/propostas/nova', () => import('./app/(protected)/eletropostos/propostas/nova/page')),
    pagina(authed, '/indicadores', () => import('./app/(protected)/indicadores/page')),
    pagina(authed, '/minha-empresa', () => import('./app/(protected)/minha-empresa/page')),
    pagina(authed, '/parceiros', () => import('./app/(protected)/parceiros/page')),
    pagina(authed, '/parceiros/$id', () => import('./app/(protected)/parceiros/[id]/page')),
    pagina(authed, '/parceiros/novo', () => import('./app/(protected)/parceiros/novo/page')),
    pagina(authed, '/propostas', () => import('./app/(protected)/propostas/page')),
    pagina(authed, '/propostas/$id', () => import('./app/(protected)/propostas/[id]/page')),
    pagina(authed, '/propostas/nova', () => import('./app/(protected)/propostas/nova/page')),
    pagina(authed, '/relatorios/associados', () => import('./app/(protected)/relatorios/associados/page')),
    pagina(authed, '/relatorios/financeiro', () => import('./app/(protected)/relatorios/financeiro/page')),
    pagina(authed, '/relatorios/renovacoes', () => import('./app/(protected)/relatorios/renovacoes/page')),
    pagina(authed, '/renovacoes', () => import('./app/(protected)/renovacoes/page')),
    pagina(authed, '/renovacoes/$id', () => import('./app/(protected)/renovacoes/[id]/page')),
    pagina(authed, '/sinistros', () => import('./app/(protected)/sinistros/page')),
    pagina(authed, '/sinistros/$id', () => import('./app/(protected)/sinistros/[id]/page')),
    pagina(authed, '/solicitacoes', () => import('./app/(protected)/solicitacoes/page')),
    pagina(authed, '/solicitacoes/$id', () => import('./app/(protected)/solicitacoes/[id]/page')),
    pagina(authed, '/usuarios', () => import('./app/(protected)/usuarios/page')),
    pagina(authed, '/usuarios/$id', () => import('./app/(protected)/usuarios/[id]/page')),
];

const legados = Object.entries(REDIRECTS).map(([de, para]) => redirecionar(root, de, para));

export const routeTree = root.addChildren([...publicas, ...legados, authed.addChildren(protegidas)]);

export const router = createRouter({
    routeTree,
    // Query crua (T07 §3.4/§6.2): `?valor_bem=1000` continua string, `a,b` não é reescrito.
    parseSearch: (s) => Object.fromEntries(new URLSearchParams(s)),
    stringifySearch: (o) => {
        const q = new URLSearchParams(o as Record<string, string>).toString();
        return q ? `?${q}` : '';
    },
});
